/**
 * FuehrerscheinScanStep — Führerschein-Scan (Vorder- und Rückseite)
 *
 * Zwei-Phasen-Scan: zuerst Vorderseite (1/2), dann Rückseite (2/2).
 * Overlay im ID-1-Querformat (85.6 × 53.98 mm → Seitenverhältnis ~1.586).
 * Nach Klassen-OCR: Hinweis wenn Klasse B fehlt (kein Hard-Block).
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, CheckCircle2, AlertCircle, RefreshCw, ArrowLeft, SkipForward, AlertTriangle } from 'lucide-react';
import type { FuehrerscheinVorderseiteFelder, FuehrerscheinKlasse } from '@/lib/kiosk-ocr';
import { hatKlasseB } from '@/lib/kiosk-ocr';

type ScanSeite = 'vorderseite' | 'rueckseite';
type ScanPhase = 'camera' | 'analyzing' | 'success' | 'error' | 'nocamera';

export interface FuehrerscheinErgebnis {
  vorderseite: FuehrerscheinVorderseiteFelder;
  klassen: FuehrerscheinKlasse[];
  fuehrerscheinUrl: string;
  sessionId: string;
}

interface Props {
  onComplete: (ergebnis: FuehrerscheinErgebnis) => void;
  onSkip: () => void;
  onBack: () => void;
  sessionId?: string;
}

const BRAND_BLUE = '#1E5FB7';
const BRAND_GREEN = '#3DB54A';

// ID-1 landscape aspect ratio: 85.6mm / 53.98mm ≈ 1.586
const ID1_ASPECT = 85.6 / 53.98;

export function FuehrerscheinScanStep({ onComplete, onSkip, onBack, sessionId }: Props) {
  const [seite, setSeite] = useState<ScanSeite>('vorderseite');
  const [phase, setPhase] = useState<ScanPhase>('camera');
  const [errorMsg, setErrorMsg] = useState('');
  const [vorderseiteFelder, setVorderseiteFelder] = useState<FuehrerscheinVorderseiteFelder | null>(null);
  const [vorderseiteUrl, setVorderseiteUrl] = useState('');
  const [klassen, setKlassen] = useState<FuehrerscheinKlasse[] | null>(null);
  const [klasseBWarnung, setKlasseBWarnung] = useState(false);
  const sessionIdRef = useRef<string | null>(sessionId ?? null);

  useEffect(() => {
    if (sessionId) sessionIdRef.current = sessionId;
  }, [sessionId]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let active = true;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        });
        if (!active) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch {
        if (active) setPhase('nocamera');
      }
    }

    if (phase === 'camera') startCamera();

    return () => {
      active = false;
      if (phase !== 'camera') stopCamera();
    };
  }, [phase, stopCamera]);

  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  const captureAndAnalyze = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext('2d')?.drawImage(video, 0, 0);

    const base64 = canvas.toDataURL('image/jpeg', 0.92).replace('data:image/jpeg;base64,', '');

    stopCamera();
    setPhase('analyzing');

    try {
      const res = await fetch('/api/kiosk/ocr-fuehrerschein', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: base64,
          mimeType: 'image/jpeg',
          session_id: sessionIdRef.current ?? undefined,
          seite,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      sessionIdRef.current = data.session_id ?? sessionIdRef.current;

      if (seite === 'vorderseite') {
        setVorderseiteFelder(data.felder);
        setVorderseiteUrl(data.fuehrerschein_url ?? '');
      } else {
        const receivedKlassen: FuehrerscheinKlasse[] = data.klassen ?? [];
        setKlassen(receivedKlassen);
        if (!hatKlasseB(receivedKlassen)) setKlasseBWarnung(true);
      }

      setPhase('success');
    } catch (err) {
      console.error('[FuehrerscheinScan] OCR-Fehler:', err);
      setErrorMsg('Die Analyse ist fehlgeschlagen. Bitte erneut versuchen.');
      setPhase('error');
    }
  }, [seite, stopCamera]);

  const retry = useCallback(() => {
    setErrorMsg('');
    setPhase('camera');
  }, []);

  const handleVorderseiteBestätigen = useCallback(() => {
    setPhase('camera');
    setSeite('rueckseite');
  }, []);

  const handleRueckseiteBestätigen = useCallback(() => {
    if (!vorderseiteFelder || klassen === null) return;
    onComplete({
      vorderseite: vorderseiteFelder,
      klassen,
      fuehrerscheinUrl: vorderseiteUrl,
      sessionId: sessionIdRef.current ?? '',
    });
  }, [vorderseiteFelder, klassen, vorderseiteUrl, onComplete]);

  const seiteLabel = seite === 'vorderseite' ? '1/2 — Vorderseite' : '2/2 — Rückseite';
  const isRueckseite = seite === 'rueckseite';

  return (
    <div data-testid="fuehrerschein-scan-step" className="flex flex-col items-center gap-6 w-full">
      <AnimatePresence mode="wait">

        {/* ── Kamera-Phase ──────────────────────────────────────── */}
        {(phase === 'camera' || phase === 'nocamera') && (
          <motion.div
            key={`camera-${seite}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-5 w-full"
          >
            <div className="text-center">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md"
                style={{ background: BRAND_BLUE }}
              >
                <Camera size={26} className="text-white" />
              </div>
              <h2 className="text-[22px] font-bold text-gray-900">Führerschein scannen</h2>
              <div
                className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-[12px] font-semibold text-white"
                style={{ background: BRAND_BLUE }}
                data-testid="scan-progress-indicator"
              >
                {seiteLabel}
              </div>
              <p className="text-[14px] text-gray-500 mt-2 max-w-xs mx-auto leading-relaxed">
                {isRueckseite
                  ? 'Drehen Sie den Führerschein um und positionieren Sie die Rückseite.'
                  : 'Positionieren Sie die Vorderseite Ihres Führerscheins im Rahmen.'}
              </p>
            </div>

            {phase === 'nocamera' ? (
              <div className="w-full bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 text-center">
                <AlertCircle size={32} className="text-amber-500 mx-auto mb-3" />
                <p className="text-[14px] font-semibold text-amber-800 mb-1">Kamerazugriff nicht möglich</p>
                <p className="text-[13px] text-amber-700">
                  Bitte erlauben Sie den Kamerazugriff in den Browser-Einstellungen.
                </p>
              </div>
            ) : (
              /* ID-1 landscape overlay (aspect ratio 85.6:53.98 ≈ 1.586) */
              <div
                className="relative rounded-2xl overflow-hidden bg-black w-full shadow-xl"
                style={{ aspectRatio: '4/3', maxHeight: 340 }}
                data-testid="camera-viewfinder"
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="absolute inset-0 w-full h-full object-cover"
                />

                <div className="absolute inset-0 bg-black/40" />

                {/* ID-1 format overlay (landscape, proportional to viewport) */}
                <div
                  className="absolute"
                  data-testid="fuehrerschein-overlay-frame"
                  style={{
                    top: '12%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80%',
                    aspectRatio: `${ID1_ASPECT}`,
                    border: '2px solid rgba(255,255,255,0.9)',
                    borderRadius: 6,
                    boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
                  }}
                >
                  {/* Corner markers */}
                  {[
                    { top: -2, left: -2, borderRight: 'none', borderBottom: 'none' },
                    { top: -2, right: -2, borderLeft: 'none', borderBottom: 'none' },
                    { bottom: -2, left: -2, borderRight: 'none', borderTop: 'none' },
                    { bottom: -2, right: -2, borderLeft: 'none', borderTop: 'none' },
                  ].map((style, i) => (
                    <div
                      key={i}
                      className="absolute"
                      style={{
                        width: 18, height: 18,
                        border: `3px solid ${BRAND_BLUE}`,
                        borderRadius: 2,
                        ...style,
                      }}
                    />
                  ))}

                  {/* Animated scan stripe */}
                  <motion.div
                    className="absolute left-0 right-0 h-0.5"
                    style={{ background: `linear-gradient(90deg, transparent, ${BRAND_BLUE}, transparent)` }}
                    animate={{ top: ['5%', '95%', '5%'] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: 'linear' }}
                  />
                </div>

                <p className="absolute bottom-3 left-0 right-0 text-center text-white text-[12px] font-medium tracking-wide">
                  Führerschein im Querformat positionieren
                </p>
              </div>
            )}

            <canvas ref={canvasRef} className="hidden" />

            <div className="flex gap-3 w-full">
              <button
                onClick={seite === 'vorderseite' ? onBack : undefined}
                style={{ display: seite === 'vorderseite' ? undefined : 'none', minHeight: 52 }}
                className="flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
              >
                <ArrowLeft size={16} /> Zurück
              </button>

              <button
                onClick={captureAndAnalyze}
                disabled={phase === 'nocamera'}
                data-testid="fuehrerschein-capture-button"
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 52 }}
              >
                <Camera size={18} />
                Foto aufnehmen
              </button>
            </div>

            <button
              onClick={onSkip}
              data-testid="fuehrerschein-skip-button"
              className="flex items-center gap-1.5 text-[13px] text-gray-400 hover:text-gray-600 transition-colors"
            >
              <SkipForward size={14} /> Überspringen
            </button>
          </motion.div>
        )}

        {/* ── Analyse-Phase ─────────────────────────────────────── */}
        {phase === 'analyzing' && (
          <motion.div
            key="analyzing"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-6 py-8 text-center"
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg"
              style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)` }}
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              >
                <RefreshCw size={36} className="text-white" />
              </motion.div>
            </div>
            <div>
              <h2 className="text-[24px] font-bold text-gray-900">Daten werden erkannt</h2>
              <p className="text-[15px] text-gray-500 mt-2">
                Führerschein-{seite === 'vorderseite' ? 'Vorderseite' : 'Rückseite'} wird analysiert…
              </p>
            </div>
            <div className="flex gap-2 mt-2">
              {[0, 1, 2].map(i => (
                <motion.div
                  key={i}
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: BRAND_BLUE }}
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.4 }}
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* ── Erfolgs-Phase Vorderseite ─────────────────────────── */}
        {phase === 'success' && seite === 'vorderseite' && vorderseiteFelder && (
          <motion.div
            key="success-vorderseite"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex flex-col gap-5 w-full"
          >
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
                style={{ background: '#ECFDF5' }}
              >
                <CheckCircle2 size={36} className="text-emerald-600" />
              </motion.div>
              <h2 className="text-[22px] font-bold text-gray-900">Vorderseite erkannt</h2>
              <p className="text-[14px] text-gray-500 mt-1">Weiter mit der Rückseite.</p>
            </div>

            <div
              className="bg-white rounded-2xl border-2 border-gray-200 p-5 space-y-3 shadow-sm"
              data-testid="fuehrerschein-vorderseite-felder"
            >
              <FelderRow label="Nachname" value={vorderseiteFelder.nachname} />
              <FelderRow label="Vorname" value={vorderseiteFelder.vorname} />
              <FelderRow label="Geburtsdatum" value={vorderseiteFelder.geburtsdatum} />
              <FelderRow label="FS-Nummer" value={vorderseiteFelder.fuehrerschein_nr} mono />
              <FelderRow label="Gültig Kl. B bis" value={vorderseiteFelder.gueltigkeitsdatum_b} />
            </div>

            <div className="flex gap-3">
              <button
                onClick={retry}
                className="flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
                style={{ minHeight: 52 }}
              >
                <RefreshCw size={16} /> Erneut scannen
              </button>

              <button
                onClick={handleVorderseiteBestätigen}
                data-testid="fuehrerschein-vorderseite-weiter"
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all"
                style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 52 }}
              >
                Rückseite scannen →
              </button>
            </div>
          </motion.div>
        )}

        {/* ── Erfolgs-Phase Rückseite ────────────────────────────── */}
        {phase === 'success' && seite === 'rueckseite' && klassen !== null && (
          <motion.div
            key="success-rueckseite"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex flex-col gap-5 w-full"
          >
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
                style={{ background: '#ECFDF5' }}
              >
                <CheckCircle2 size={36} className="text-emerald-600" />
              </motion.div>
              <h2 className="text-[22px] font-bold text-gray-900">Rückseite erkannt</h2>
              <p className="text-[14px] text-gray-500 mt-1">
                {klassen.length > 0
                  ? `${klassen.length} Klasse${klassen.length !== 1 ? 'n' : ''} gefunden`
                  : 'Keine Klassen erkannt'}
              </p>
            </div>

            {/* Klasse-B-Warnung */}
            {klasseBWarnung && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="klasse-b-warnung"
                className="flex items-start gap-3 p-4 rounded-2xl border-2 border-amber-200 bg-amber-50"
              >
                <AlertTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[14px] font-semibold text-amber-800">Keine Klasse B gefunden</p>
                  <p className="text-[13px] text-amber-700 mt-0.5">
                    Bitte prüfen Sie, ob Sie zur Fahrt berechtigt sind. Ein Mitarbeiter wird dies prüfen.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Klassen-Liste */}
            {klassen.length > 0 && (
              <div
                className="bg-white rounded-2xl border-2 border-gray-200 p-4 shadow-sm"
                data-testid="fuehrerschein-klassen-liste"
              >
                <p className="text-[12px] text-gray-400 mb-3 font-semibold uppercase tracking-wide">Führerscheinklassen</p>
                <div className="flex flex-wrap gap-2">
                  {klassen.map(k => (
                    <span
                      key={k.klasse}
                      className="inline-flex flex-col items-center px-3 py-1.5 rounded-xl text-[13px] font-bold"
                      style={{
                        background: k.klasse === 'B' ? '#DBEAFE' : '#F3F4F6',
                        color: k.klasse === 'B' ? BRAND_BLUE : '#374151',
                      }}
                    >
                      {k.klasse}
                      {k.ausstellungsdatum && (
                        <span className="text-[10px] font-normal opacity-60 mt-0.5">{k.ausstellungsdatum}</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={retry}
                className="flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
                style={{ minHeight: 52 }}
              >
                <RefreshCw size={16} /> Erneut scannen
              </button>

              <button
                onClick={handleRueckseiteBestätigen}
                data-testid="fuehrerschein-rueckseite-weiter"
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all"
                style={{ background: `linear-gradient(135deg, ${BRAND_GREEN}, #2D9E3A)`, minHeight: 52 }}
              >
                <CheckCircle2 size={18} />
                Weiter
              </button>
            </div>
          </motion.div>
        )}

        {/* ── Fehler-Phase ──────────────────────────────────────── */}
        {phase === 'error' && (
          <motion.div
            key="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-5 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center shadow-md">
              <AlertCircle size={32} className="text-red-500" />
            </div>
            <div>
              <h2 className="text-[20px] font-bold text-gray-900">Scan fehlgeschlagen</h2>
              <p className="text-[14px] text-gray-500 mt-1">{errorMsg}</p>
            </div>
            <button
              onClick={retry}
              data-testid="fuehrerschein-retry-button"
              className="flex items-center gap-2 px-7 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all"
              style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 52 }}
            >
              <RefreshCw size={18} /> Erneut versuchen
            </button>
            <button
              onClick={onSkip}
              className="text-[13px] text-gray-400 hover:text-gray-600 transition-colors"
            >
              Schritt überspringen
            </button>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
}

function FelderRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[12px] text-gray-400 w-28 flex-shrink-0">{label}</span>
      <span className={`text-[14px] font-semibold text-gray-800 flex-1 ${mono ? 'font-mono' : ''}`}>
        {value || <span className="text-gray-300 font-normal">—</span>}
      </span>
    </div>
  );
}
